/**
 * App Tabs — shared wrapper for tab navigation
 * @module components/layout/AppTabs
 */

import GlobalErrorBoundary from "@components/common/errors/GlobalErrorBoundary";
import { TabContainer } from "@components/layout/tabs/TabContainer";
import type { Tab } from "@lib/tabs/types";

interface AppTabsProps {
  /** Tabs from a hook (useRouteTabs or useDashboardTabs) */
  tabs: Tab[];
  isLoading?: boolean;
  error?: Error | null;
  /** For GlobalErrorBoundary identification */
  componentName?: string;
}

/**
 * Shared wrapper — error, loading, empty, and render states
 * Used by both dashboard and module tabs
 */
export function AppTabs({
  tabs,
  isLoading = false,
  error = null,
  componentName = "AppTabs",
}: AppTabsProps) {
  if (error) {
    return (
      <div className="tab-container">
        <div className="px-4 py-3 text-error text-sm">Erreur de chargement des onglets</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="tab-container">
        <div className="flex items-center gap-3 py-2 px-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="tab-skeleton" />
          ))}
        </div>
      </div>
    );
  }

  if (!tabs.length) {
    return null;
  }

  return (
    <GlobalErrorBoundary componentName={componentName}>
      <TabContainer tabs={tabs} />
    </GlobalErrorBoundary>
  );
}
